// ABOUTME: Challenge tracking service that monitors user progress
// ABOUTME: Detects challenge completion and distributes rewards

import { CHALLENGES, getChallengeById } from '@/content/challenges'
import { useGameStore } from '@/lib/store/gameStore'
import { useModuleStore } from '@/lib/store/moduleStore'
import { useWardrobeStore } from '@/lib/store/wardrobeStore'
import type { WearLog } from '@/lib/store/wearLogStore'
import { useWearLogStore } from '@/lib/store/wearLogStore'
import type { ChallengeProgress } from '@/types/models'
import type { IChallengeTracker, UserAchievementStats } from '@/types/services'

function getConsecutiveUniqueDays(logs: WearLog[]): number {
  if (logs.length === 0) return 0

  const uniqueDates = [...new Set(logs.map((l) => l.date))].sort().reverse()
  let streak = 1

  for (let i = 1; i < uniqueDates.length; i++) {
    const prev = new Date(uniqueDates[i - 1])
    const curr = new Date(uniqueDates[i])
    const diffDays = Math.round((prev.getTime() - curr.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays !== 1) break

    const prevItemIds = logs.filter((l) => l.date === uniqueDates[i - 1]).flatMap((l) => l.itemIds)
    const currItemIds = logs.filter((l) => l.date === uniqueDates[i]).flatMap((l) => l.itemIds)
    const prevSet = prevItemIds.sort().join(',')
    const currSet = currItemIds.sort().join(',')
    if (prevSet === currSet) break

    streak++
  }

  return streak
}

export class ChallengeTracker implements IChallengeTracker {
  private progress: Map<string, ChallengeProgress> = new Map()
  private userId: string

  constructor(userId: string = 'local-user') {
    this.userId = userId
    this.loadProgress()
  }

  /**
   * Load saved progress from localStorage
   */
  private loadProgress(): void {
    try {
      const saved = localStorage.getItem('challenge-progress')
      if (saved) {
        const data = JSON.parse(saved)
        this.progress = new Map(Object.entries(data))
      }
    } catch (error) {
      console.error('Failed to load challenge progress:', error)
    }
  }

  /**
   * Save progress to localStorage
   */
  private saveProgress(): void {
    try {
      const data = Object.fromEntries(this.progress)
      localStorage.setItem('challenge-progress', JSON.stringify(data))
    } catch (error) {
      console.error('Failed to save challenge progress:', error)
    }
  }

  /**
   * Start tracking a challenge
   */
  async startChallenge(challengeId: string): Promise<ChallengeProgress> {
    const challenge = getChallengeById(challengeId)
    if (!challenge) {
      throw new Error(`Challenge not found: ${challengeId}`)
    }

    // Check if already tracking
    const existing = this.progress.get(challengeId)
    if (existing && existing.status !== 'completed') {
      return existing
    }

    // Initialize progress tracking
    const progress: ChallengeProgress = {
      challengeId,
      userId: this.userId,
      status: 'in_progress',
      requirements: challenge.requirements.map((req) => ({
        requirementId: req.id,
        type: req.type,
        current: 0,
        target: req.target,
        completed: false,
        lastUpdated: new Date(),
      })),
      startedAt: new Date(),
      metadata: {},
    }

    this.progress.set(challengeId, progress)
    this.saveProgress()

    return progress
  }

  /**
   * Update progress for a specific requirement
   */
  async updateProgress(
    challengeId: string,
    requirementId: string,
    increment: number = 1
  ): Promise<ChallengeProgress> {
    let progress = this.progress.get(challengeId)

    // Auto-start if not started
    if (!progress) {
      progress = await this.startChallenge(challengeId)
    }

    // Find and update requirement
    const requirement = progress.requirements.find((r) => r.requirementId === requirementId)

    if (!requirement) {
      throw new Error(`Requirement not found: ${requirementId}`)
    }

    // Update progress
    requirement.current = Math.min(requirement.current + increment, requirement.target)
    requirement.completed = requirement.current >= requirement.target
    requirement.lastUpdated = new Date()

    // Check if all requirements completed
    const allCompleted = progress.requirements.every((r) => r.completed)
    if (allCompleted && progress.status !== 'completed') {
      await this.completeChallenge(challengeId)
    }

    this.progress.set(challengeId, progress)
    this.saveProgress()

    return progress
  }

  /**
   * Check if challenge is completed
   */
  async checkCompletion(challengeId: string): Promise<boolean> {
    const progress = this.progress.get(challengeId)
    if (!progress) return false

    return progress.requirements.every((r) => r.completed)
  }

  /**
   * Get current progress for a challenge
   */
  async getProgress(challengeId: string): Promise<ChallengeProgress | null> {
    return this.progress.get(challengeId) || null
  }

  /**
   * Get all active challenges
   */
  async getActiveChallenges(): Promise<ChallengeProgress[]> {
    return Array.from(this.progress.values()).filter((p) => p.status === 'in_progress')
  }

  /**
   * Complete challenge and distribute rewards
   */
  async completeChallenge(challengeId: string): Promise<void> {
    const progress = this.progress.get(challengeId)
    if (!progress) {
      throw new Error(`Challenge not in progress: ${challengeId}`)
    }

    const challenge = getChallengeById(challengeId)
    if (!challenge) {
      throw new Error(`Challenge definition not found: ${challengeId}`)
    }

    // Update progress status
    progress.status = 'completed'
    progress.completedAt = new Date()
    this.progress.set(challengeId, progress)
    this.saveProgress()

    // Award XP and update game store
    const gameStore = useGameStore.getState()
    gameStore.addExperience(challenge.xpReward)

    // Add completion activity
    gameStore.addActivity({
      type: 'challenge_completed',
      message: `Completed "${challenge.title}"!`,
      experienceGained: challenge.xpReward,
    })

    console.log(`✅ Challenge completed: ${challenge.title} (+${challenge.xpReward} XP)`)
  }

  /**
   * Monitor wardrobe actions for challenge progress
   */
  onItemScanned(category?: string): void {
    // Find challenges with scan_items requirements
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'scan_items') {
          // Check if category matches (if specified)
          const categoryMatch = !req.metadata?.category || req.metadata.category === category

          if (categoryMatch) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }
      })
    })
  }

  /**
   * Monitor outfit creation for challenge progress
   */
  onOutfitCreated(metadata?: Record<string, unknown>): void {
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'create_outfit') {
          // Check metadata matches (if specified)
          let matches = true

          if (req.metadata) {
            for (const [key, value] of Object.entries(req.metadata)) {
              if (metadata?.[key] !== value) {
                matches = false
                break
              }
            }
          }

          if (matches) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }
      })
    })
  }

  /**
   * Monitor quiz completion for challenge progress
   */
  onQuizCompleted(quizId: string, score: number): void {
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'complete_quiz') {
          const requiredQuiz = req.metadata?.quizId
          const minScore = typeof req.metadata?.minScore === 'number' ? req.metadata.minScore : 0

          if (requiredQuiz === quizId && score >= minScore) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }
      })
    })
  }

  /**
   * Monitor photo uploads for challenge progress
   */
  onPhotoUploaded(photoType?: string): void {
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'upload_photo') {
          const requiredType = req.metadata?.photoType
          const typeMatches = !requiredType || requiredType === photoType

          if (typeMatches) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }
      })
    })
  }

  onOutfitLogged(wearLog: WearLog, neglectedItemIds: string[] = []): void {
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'log_outfit') {
          this.updateProgress(challenge.id, req.id, 1).catch(console.error)
        }

        if (req.type === 'use_neglected_item' && neglectedItemIds.length > 0) {
          const usedNeglected = wearLog.itemIds.some((id) => neglectedItemIds.includes(id))
          if (usedNeglected) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }

        if (req.type === 'no_repeat_week') {
          const logs = useWearLogStore.getState().logs
          const recentDays = getConsecutiveUniqueDays(logs)
          if (recentDays >= 7) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }
      })
    })
  }

  onVersatileItemDetected(_itemId: string, outfitCount: number): void {
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'versatile_item') {
          const needed = (req.metadata?.outfitCount as number) || 3
          if (outfitCount >= needed) {
            this.updateProgress(challenge.id, req.id, 1).catch(console.error)
          }
        }
      })
    })
  }

  onOccasionOutfitCreated(_occasion: string): void {
    CHALLENGES.forEach((challenge) => {
      challenge.requirements.forEach((req) => {
        if (req.type === 'occasion_outfit') {
          this.updateProgress(challenge.id, req.id, 1).catch(console.error)
        }
      })
    })
  }

  /**
   * Get completion percentage for a challenge
   */
  getCompletionPercentage(challengeId: string): number {
    const progress = this.progress.get(challengeId)
    if (!progress) return 0

    const totalRequirements = progress.requirements.length
    const completedRequirements = progress.requirements.filter((r) => r.completed).length

    return Math.floor((completedRequirements / totalRequirements) * 100)
  }

  /**
   * Get user stats for achievement checking
   */
  getUserStats(): UserAchievementStats {
    const gameState = useGameStore.getState()
    const wardrobeState = useWardrobeStore.getState()
    const moduleState = useModuleStore.getState()

    return {
      level: gameState.level,
      totalXP: gameState.experiencePoints,
      challengesCompleted: gameState.totalChallengesCompleted,
      modulesCompleted: moduleState.completedModules.length,
      itemsScanned: wardrobeState.stats.totalItems,
      streakDays: gameState.currentStreak,
      perfectQuizzes: 0, // TODO: Track perfect quiz scores
      outfitsCreated: wardrobeState.outfits.length,
    }
  }

  /**
   * Reset all progress (for testing)
   */
  reset(): void {
    this.progress.clear()
    localStorage.removeItem('challenge-progress')
  }
}

// Singleton instance
let trackerInstance: ChallengeTracker | null = null

/**
 * Get challenge tracker instance
 */
export function getChallengeTracker(): ChallengeTracker {
  if (!trackerInstance) {
    trackerInstance = new ChallengeTracker()
  }
  return trackerInstance
}

/**
 * Hook into wardrobe store to track scans
 */
export function setupChallengeTracking(): void {
  const tracker = getChallengeTracker()

  useWardrobeStore.subscribe((state, prevState) => {
    if (state.items.length > prevState.items.length) {
      const newItem = state.items[state.items.length - 1]
      tracker.onItemScanned(newItem.category)
    }

    if (state.outfits.length > prevState.outfits.length) {
      const newOutfit = state.outfits[state.outfits.length - 1]
      tracker.onOutfitCreated({
        occasion: newOutfit.occasion,
      })
    }
  })

  useWearLogStore.subscribe((state, prevState) => {
    if (state.logs.length > prevState.logs.length) {
      const newLog = state.logs[state.logs.length - 1]

      const neglectedIds = getNeglectedItemIds()
      tracker.onOutfitLogged(newLog, neglectedIds)
    }
  })
}

function getNeglectedItemIds(thresholdDays: number = 30): string[] {
  const items = useWardrobeStore.getState().items
  const logs = useWearLogStore.getState().logs
  const now = new Date()

  return items
    .filter((item) => {
      const itemLogs = logs.filter((l) => l.itemIds.includes(item.id))
      if (itemLogs.length === 0) return true
      const lastDate = itemLogs.reduce((max, l) => (l.date > max ? l.date : max), '')
      const daysSince = Math.floor(
        (now.getTime() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24)
      )
      return daysSince >= thresholdDays
    })
    .map((item) => item.id)
}
